import type { BookingType, BookingTypeRules, VenueConfig } from "../../common/jsonRpcTypes";
import { getRulesForDate, findDateOverrideRules, getOpeningTimesForDate, getDmnRulesForWeekday } from "./bookingRules";

// ─── Constants ───

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

// ─── Types ───

/** The computed date range within which bookings are allowed. */
export interface AvailableDateRange {
  minDate: Date;
  maxDate: Date;
}

/** Result of checking whether a single day is available. */
export interface DayAvailabilityResult {
  available: boolean;
  reason?: string;
}

/** Map of date strings (YYYY-MM-DD) to their availability result. */
export type MonthAvailabilityMap = Record<string, DayAvailabilityResult>;

// ─── Internal Helpers ───

/** Parse a date string (YYYY-MM-DD) into a Date at local midnight. */
function parseDate(dateStr: string): Date {
  const parts = dateStr.split("T")[0].split("-");
  if (parts.length === 3) {
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0, 0);
  }
  return new Date(dateStr);
}

/** Get today at local midnight. */
function todayAtMidnight(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}

/** Get the lowercase weekday name for a Date. */
function getWeekday(date: Date): string {
  return WEEKDAYS[date.getDay()];
}

/** Format a Date to 'YYYY-MM-DD'. */
function formatDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ─── Exported Functions ───

/**
 * Compute the selectable date range based on advance booking constraints.
 *
 * - `advance_bookings_min_days`: earliest allowed date is today + N days
 * - `advance_bookings_max_days`: latest allowed date is today + N days
 */
export function getAvailableDateRange(bookingType: BookingType | undefined): AvailableDateRange {
  const today = todayAtMidnight();

  const minDays = bookingType?.advance_bookings_min_days ?? 0;
  const maxDays = bookingType?.advance_bookings_max_days ?? 365;

  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + minDays);

  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxDays);

  return { minDate, maxDate };
}

/**
 * Find a matching num_people override for the given guest count.
 *
 * Mirrors `BookingTypeFactory.makeNumPeopleRules()`.
 */
function getNumPeopleOverride(bookingType: BookingType, numPeople: number | undefined): BookingTypeRules | null {
  if (numPeople == null || !bookingType.num_people_overrides) return null;

  for (const override of bookingType.num_people_overrides) {
    if (numPeople >= override.num_people_from && numPeople <= override.num_people_to) {
      return buildNumPeopleRules(override);
    }
  }

  return null;
}

/** Copy only the defined override fields into a BookingTypeRules object. */
function buildNumPeopleRules(override: {
  booking_available?: boolean;
  min_duration?: number;
  max_duration?: number;
  min_people?: number;
  max_people?: number;
}): BookingTypeRules {
  const result: BookingTypeRules = {};
  if (override.booking_available !== undefined) result.booking_available = override.booking_available;
  if (override.min_duration !== undefined) result.min_duration = override.min_duration;
  if (override.max_duration !== undefined) result.max_duration = override.max_duration;
  if (override.min_people !== undefined) result.min_people = override.min_people;
  if (override.max_people !== undefined) result.max_people = override.max_people;
  return result;
}

/**
 * Determine whether a specific day is available for bookings under a given
 * booking type.
 *
 * Rule resolution (weekday / DMN / date-override merging) is delegated to
 * `getRulesForDate()` from bookingRules. This function additionally enforces:
 *
 * 1. Date range boundaries (`advance_bookings_min_days` / `advance_bookings_max_days`)
 * 2. Date-override precedence: a date override's `booking_available` always wins
 *    over weekday/DMN rules, EXCEPT when DMN explicitly disables the day
 *    (in which case the override is skipped entirely)
 * 3. Num people overrides (`num_people_overrides`)
 *
 * @param dateStr  - 'YYYY-MM-DD' date string
 * @param bookingType - The selected booking type configuration
 * @param numPeople - Optional guest count for num_people_overrides
 * @param source - Booking source ('designmynight' | 'partner' etc). DMN rules only apply to DesignMyNight sources
 */

/** Check advance booking date boundaries. */
function checkAdvanceDateBounds(date: Date, today: Date, bookingType: BookingType): DayAvailabilityResult | null {
  if (bookingType.advance_bookings_max_days != null) {
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + bookingType.advance_bookings_max_days);
    if (date > maxDate) {
      return { available: false, reason: `Bookable up to ${bookingType.advance_bookings_max_days} days in advance` };
    }
  }

  if (bookingType.advance_bookings_min_days != null && bookingType.advance_bookings_min_days > 0) {
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + bookingType.advance_bookings_min_days);
    if (date < minDate) {
      return { available: false, reason: `Must book ${bookingType.advance_bookings_min_days} days in advance` };
    }
  }

  return null;
}

/** Check date-override precedence, skipping when DMN explicitly disables the day. */
function checkDateOverridePrecedence(
  bookingType: BookingType,
  date: Date,
  weekday: string,
  bookingSource: string,
): DayAvailabilityResult | null {
  const dmnDisablesDay = getDmnRulesForWeekday(bookingType, weekday, bookingSource)?.booking_available === false;

  if (dmnDisablesDay) return null;

  const dateOverride = findDateOverrideRules(bookingType, date);
  if (dateOverride?.booking_available === false) {
    return { available: false, reason: `${bookingType.name} bookings unavailable on this date` };
  }
  if (dateOverride?.booking_available === true) {
    return { available: true };
  }

  return null;
}

/** Validate guest count against num_people_overrides and merged booking rules. */
function checkGuestCountConstraints(
  bookingType: BookingType,
  numPeople: number,
  mergedRules: BookingTypeRules,
): DayAvailabilityResult | null {
  const numPeopleOverride = getNumPeopleOverride(bookingType, numPeople);
  if (numPeopleOverride) {
    if (numPeopleOverride.booking_available === false) {
      return { available: false, reason: `Not available for ${numPeople} guests` };
    }
    if (numPeopleOverride.min_people != null && numPeople < numPeopleOverride.min_people) {
      return { available: false, reason: `Minimum ${numPeopleOverride.min_people} guests required` };
    }
    if (numPeopleOverride.max_people != null && numPeople > numPeopleOverride.max_people) {
      return { available: false, reason: `Maximum ${numPeopleOverride.max_people} guests` };
    }
    return null;
  }

  if (mergedRules.min_people != null && numPeople < mergedRules.min_people) {
    return { available: false, reason: `Minimum ${mergedRules.min_people} guests required` };
  }
  if (mergedRules.max_people != null && numPeople > mergedRules.max_people) {
    return { available: false, reason: `Maximum ${mergedRules.max_people} guests` };
  }

  return null;
}

export function isDayAvailableForBookingType(
  dateStr: string,
  bookingType: BookingType | undefined,
  numPeople?: number,
  source?: string,
  venueConfig?: VenueConfig | null,
): DayAvailabilityResult {
  const date = parseDate(dateStr);
  const today = todayAtMidnight();

  if (date < today) {
    return { available: false };
  }

  if (!bookingType) {
    return { available: true };
  }

  const advanceResult = checkAdvanceDateBounds(date, today, bookingType);
  if (advanceResult) return advanceResult;

  // Ensure the weekday has at least some booking rules configured
  const bookingSource = source ?? "partner";
  const weekday = getWeekday(date);
  const hasWeekdayRules = bookingType.booking_rules?.[weekday] != null;
  const hasDmnRules = getDmnRulesForWeekday(bookingType, weekday, bookingSource) != null;

  if (!hasWeekdayRules && !hasDmnRules) {
    return { available: false, reason: "No booking rules for this day" };
  }

  // Delegate weekday / DMN / date-override rule merging to the shared utility
  const mergedRules = getRulesForDate(bookingType, dateStr, bookingSource);

  const overrideResult = checkDateOverridePrecedence(bookingType, date, weekday, bookingSource);
  if (overrideResult) return overrideResult;

  if (mergedRules.booking_available === false) {
    return { available: false, reason: `No ${bookingType.name} bookings on this day` };
  }

  if (numPeople != null) {
    const guestResult = checkGuestCountConstraints(bookingType, numPeople, mergedRules);
    if (guestResult) return guestResult;
  }

  // Check venue opening times — if the venue is closed on this day, disable it
  if (venueConfig) {
    const openingEntry = getOpeningTimesForDate(venueConfig, dateStr, weekday);
    if (!openingEntry || openingEntry.status === false) {
      return { available: false, reason: "Closed" };
    }
  }

  return { available: true };
}

/**
 * Compute availability for every day in a given month.
 *
 * Returns a map of 'YYYY-MM-DD' -> DayAvailabilityResult for each day
 * in the specified month.
 *
 * @param year - Full year (e.g. 2026)
 * @param month - 0-indexed month (0 = January, 11 = December)
 * @param bookingType - The selected booking type
 * @param numPeople - Optional guest count
 * @param source - Booking source ('designmynight' | 'partner' etc). DMN rules only apply to DesignMyNight sources
 */
export function getMonthAvailability(
  year: number,
  month: number,
  bookingType: BookingType | undefined,
  numPeople?: number,
  source?: string,
  venueConfig?: VenueConfig | null,
): MonthAvailabilityMap {
  const result: MonthAvailabilityMap = {};

  // Get the number of days in this month
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = formatDateString(new Date(year, month, day));
    result[dateStr] = isDayAvailableForBookingType(dateStr, bookingType, numPeople, source, venueConfig);
  }

  return result;
}

/**
 * Format a Date to 'YYYY-MM-DD' string. Exported for use by hooks/components.
 */
export function toDateString(date: Date): string {
  return formatDateString(date);
}

/**
 * Parse a 'YYYY-MM-DD' string into a local-midnight Date. Exported for use
 * by hooks/components.
 */
export function fromDateString(dateStr: string): Date {
  return parseDate(dateStr);
}
